import React from 'react';
import styled, { css } from 'styled-components';

type BadgeVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'gold' | 'gradient';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  glow?: boolean;
  style?: React.CSSProperties;
}

const variantStyles = {
  default: css`
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textSecondary};
    border: 1px solid ${({ theme }) => theme.colors.border};
  `,
  primary: css`
    background: ${({ theme }) => theme.colors.primaryLight};
    color: ${({ theme }) => theme.colors.primary};
    border: 1px solid rgba(255, 107, 53, 0.2);
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondaryLight};
    color: #B8860B;
    border: 1px solid rgba(255, 179, 71, 0.2);
  `,
  success: css`
    background: ${({ theme }) => theme.colors.successLight};
    color: ${({ theme }) => theme.colors.success};
    border: 1px solid rgba(34, 197, 94, 0.2);
  `,
  warning: css`
    background: ${({ theme }) => theme.colors.warningLight};
    color: ${({ theme }) => theme.colors.warning};
    border: 1px solid rgba(255, 179, 71, 0.2);
  `,
  error: css`
    background: ${({ theme }) => theme.colors.errorLight};
    color: ${({ theme }) => theme.colors.error};
    border: 1px solid rgba(239, 68, 68, 0.2);
  `,
  info: css`
    background: ${({ theme }) => theme.colors.infoLight};
    color: ${({ theme }) => theme.colors.info};
    border: 1px solid rgba(59, 130, 246, 0.2);
  `,
  gold: css`
    background: linear-gradient(135deg, rgba(255, 179, 71, 0.2) 0%, rgba(255, 215, 0, 0.2) 100%);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.3);
  `,
  gradient: css`
    background: ${({ theme }) => theme.colors.primaryGradient};
    color: white;
    border: none;
    font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  `,
};

const sizeStyles = {
  sm: css`
    padding: 2px 6px;
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  `,
  md: css`
    padding: 4px 10px;
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  `,
};

const StyledBadge = styled.span<{ $variant: BadgeVariant; $size: BadgeSize; $dot: boolean; $glow: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $size }) => sizeStyles[$size]}
  
  ${({ $dot }) => $dot && css`
    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      animation: pulse 2s infinite;
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
  
  ${({ $glow }) => $glow && css`
    box-shadow: 0 0 12px currentColor;
  `}
`;

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  glow = false,
  style
}) => {
  return (
    <StyledBadge $variant={variant} $size={size} $dot={dot} $glow={glow} style={style}>
      {children}
    </StyledBadge>
  );
};

// Status-specific badges
export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { variant: BadgeVariant; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    inactive: { variant: 'default', label: 'Inactive' },
    archived: { variant: 'error', label: 'Archived' },
    pending: { variant: 'warning', label: 'Pending' },
    confirmed: { variant: 'success', label: 'Confirmed' },
    cancelled: { variant: 'error', label: 'Cancelled' },
    completed: { variant: 'info', label: 'Completed' },
    no_show: { variant: 'error', label: 'No Show' },
    draft: { variant: 'default', label: 'Draft' },
    sent: { variant: 'info', label: 'Sent' },
    paid: { variant: 'success', label: 'Paid' },
    overdue: { variant: 'error', label: 'Overdue' },
    void: { variant: 'default', label: 'Void' },
    new: { variant: 'primary', label: 'New' },
    contacted: { variant: 'info', label: 'Contacted' },
    trial_scheduled: { variant: 'warning', label: 'Trial Scheduled' },
    trial_done: { variant: 'info', label: 'Trial Done' },
    converted: { variant: 'success', label: 'Converted' },
    lost: { variant: 'error', label: 'Lost' },
  };

  const config = statusConfig[status] || { variant: 'default' as BadgeVariant, label: status };

  return (
    <Badge variant={config.variant} dot>
      {config.label}
    </Badge>
  );
};

